import { ProcessTemplate } from "processTemplates/model/ProcessTemplate";

export interface IProcessData {
    triggerData: any[];
    uniqueIdentifier?: string;
    version?: number;
    majorVersion?: number;
    minorVersion?: number;
    isDraft?: boolean;
    draftId?: number;
    idOfBaseProcessForDraft?: number;
    majorMinorVersions?: IMajorMinorVersions[];
    isNotLatestVersion?: boolean;
    id: number;
    name: string;
    tagString: string;
    description: string;
    bpmn: string;
    elementParameters: IElement[];
    modified?: string;
    modifier?: string;
    changeDescription: string;
    manualTrigger?: IManualTriggerParameter[];
    isMigration?: boolean;
    isSubprocess?: boolean;
    obsoleteTasks?: ITaskData[];
    activateIfNew?: boolean;
    apiSpecId: number | null;
    frendsVersion?: string;
    targetFramework?: string;
    isForMonitoringRule?: boolean;
    validationOnly?: boolean; // Only used for validating the process
    cancellationDisabled? : boolean;
    preserveReferencesAtCheckpoint : boolean;
    processVariables: IProcessVariable[];
    processTemplate?: ProcessTemplate;
    processTemplateDeviated?: Date;
    documentationMarkdown?: string;
}

export interface IAutoSavedProcessData extends IProcessData {
    autoSaved?: string;
}

export interface ISubProcessData {
    version: string;
    name: string;
    description: string;
    uniqueIdentifier: string;
    manualTriggerParameters: IManualTriggerParameter[];
}

export interface IMajorMinorVersions {
    majorVersion: number;
    minorVersion: number;
}

export interface INodeData {
    $type: string;
    nodeId: string;
    position: { top: number, left: number };
}

export interface IContainerNodeData extends INodeData {
    nodes: INodeData[];
}

export interface IElementDictionary {
    [elementId: string]: IElement;
}

export interface IManualTriggerParameter extends IParameterSchema {
    invalidString?: string;
    nonStringifiedDefaultValue?: string;
    isInvalid?: boolean;
}

export interface IElement {
    id: string;
    name?: string;
    type: ElementType;
    parameters: IParameters;
    selectedTypeId?: string;
    isDefault?: boolean;
    promoteResultAs?: string;
    description?: string;
    shouldNotLogResult?: boolean;
    // These should be moved into ITaskElement when refactored
    shouldRetry?: boolean;
    maxRetryCount?: number;
    shouldDispose?: boolean;
}

export interface IProcessVariable {
    id: number;
    name: string;
    value: string;
    displayValue: string;
    isSecret: boolean;
    mode?: string;
    description?: string;
}

export enum SelectMode {
    Select,
    Add
}

export interface IParameters {
    [parameterName: string]: any;
}

export interface IElementWithExpression extends IElement {
    parameters: {
        expression?: any;
    };
}

export interface ISequenceFlowElement extends IElementWithExpression {
    targetId: string;
    sourceId: string;
    targetType: ElementType;
    sourceType: ElementType;
}

export interface IConditionBranchElement extends ISequenceFlowElement {
    decisionElementId?: string;
    isDefault?: boolean;
}

/**
 * If you change this make sure FRENDS.ProcessCompilation/Data/ElementParameters.cs
 * public enum ElementType will be the same!
 */
export enum ElementType {
    Start = "Start",
    Task = "Task",
    NativeAi = "NativeAi", // an alias for Task
    Decision = "Decision",
    SequenceFlow = "SequenceFlow",
    ConditionBranch = "ConditionBranch",
    Return = "Return",
    Throw = "Throw",
    CallActivity = "CallActivity",
    SubProcess = "SubProcess",
    ParallelForeach = "ParallelForeach",
    SequentialForeach = "SequentialForeach",
    While = "While",
    Expression = "Expression",
    SubProcessStartNode = "SubProcessStartNode",
    Catch = "Catch",
    InclusiveGateway = "InclusiveGateway",
    InclusiveDecisionBranch = "InclusiveDecisionBranch",
    IntermediateReturn = "IntermediateReturn",
    GlobalErrorHandler = "GlobalErrorHandler",
    TestTask = "TestTask", // Only used on front end. In reality still a task. Defined here as a separate type to avoid hitting validation and filling autocompletes
    SharedState = "SharedState",
    Dmn = "Dmn", // This is just an alias for SharedState for the most part. Defined here as a separate type for backend compatibility
    DataObjectReference = "DataObjectReference",
    DataStoreReference = "DataStoreReference",
    HydrationPoint = "HydrationPoint",
    ScheduledRehydration = "ScheduledRehydration",
    SignalRehydration = "SignalRehydration",
}

export const SubProcessTypes = [
    ElementType.ParallelForeach,
    ElementType.SequentialForeach,
    ElementType.While
];

export const ConnectionTypes = [
    ElementType.InclusiveDecisionBranch,
    ElementType.ConditionBranch,
    ElementType.SequenceFlow
];

export const TypesWithReturn = [
    ElementType.Task,
    ElementType.CallActivity,
    ElementType.SubProcess,
    ElementType.InclusiveGateway,
    ElementType.Dmn,
    ElementType.NativeAi,
    ElementType.SharedState,
    ElementType.HydrationPoint
].concat(SubProcessTypes);

export const TypesWithName = [
    ElementType.Decision,
    ElementType.ConditionBranch,
    ElementType.InclusiveDecisionBranch,
    ElementType.Start,
    ElementType.Expression
].concat(TypesWithReturn);

export const TypesWithUniqueNames = [
    ElementType.Decision,
    ElementType.Start,
    ElementType.Expression
].concat(TypesWithReturn);

export const TypesWithRequiredNames = [
    ElementType.Start,
    ElementType.Task,
    ElementType.CallActivity,
    ElementType.InclusiveDecisionBranch,
    ElementType.ConditionBranch,
    ElementType.Dmn,
    ElementType.NativeAi,
    ElementType.SharedState
];

export const TypesWithSelectedType = [
    ElementType.Task,
    ElementType.Start,
    ElementType.CallActivity,
    ElementType.GlobalErrorHandler
];

export interface ITaskData {
    name: string;
    displayName: string;
    uri: string;
    packageName: string;
    argumentsJson: string;
    returnType: string;
    description: string;
    isNotLatestVersion: boolean;
    isStandard: boolean;
    isDotNet: boolean;
}

export interface IParameterSchema {
    name: string;
    type: string;
    inputType: "number" | "expression" | "text" | "password";
    description?: string;
    minimum?: number;
    maximum?: number;
    regex?: string;
    parameterType: string;
    parameterTemplate?: IParameterSchema | null;
    subParameters?: IParameterSchema[] | null;
    defaultValue: any; // may be string | number | boolean TODO: change once the callers also accept such values
    isSecret: boolean;
    displayOption?: string;
    conditionalDisplayProperty?: string;
    conditionalDisplayValues?: string[];
    defaultDisplayType?: string;
    isHidden?: boolean;
    label?: string;
}

export enum ParameterType {
    TextParameter,
    BooleanParameter,
    EnumParameter,
    EnumChoiceParameter,
    ComplexParameter,
    ArrayParameter,
    CancellationToken
}

export enum ReturnType {
    Expression = "Expression",
    HttpResult = "HttpResult",
    HttpBinaryResult = "HttpBinaryResult"
}

export enum SharedStateMethods {
    AddOrUpdate = "AddOrUpdate",
    GetOrAdd = "GetOrAdd",
    TryAdd = "TryAdd",
    TryGetValue = "TryGetValue",
    TryRemove = "TryRemove",
    TryUpdate = "TryUpdate",
    GetTTL = "GetTTL",
    UpdateTTL = "UpdateTTL"
}
